// services/square/squareApi.ts

import baseApi from "../../lib/baseApi";
import type {
  SquareItem,
  SquareResponse,
  SquareTabKey,
} from "../../types/square/squareTypes";

const ROOT = "/square";

const normalizeCursor = (cursor?: string | null) => {
  if (!cursor) return undefined;

  if (cursor.includes("://") || cursor.includes("?cursor=")) {
    try {
      const u = new URL(cursor, window.location.origin);
      return u.searchParams.get("cursor") || undefined;
    } catch {
      const m = cursor.match(/[?&]cursor=([^&]+)/);
      return m?.[1] ? decodeURIComponent(m[1]) : undefined;
    }
  }

  return cursor;
};

const normalizeSquareItem = (raw: any): SquareItem | null => {
  if (!raw || !raw.kind || typeof raw.id !== "number") {
    return null;
  }

  const preview = raw.preview || raw.payload?.preview || {};
  const meta = raw.meta || raw.payload?.meta || null;

  return {
    ...raw,
    kind: raw.kind,
    id: raw.id,
    published_at: raw.published_at ?? null,
    preview,
    meta,
    payload: raw.payload,
    owner_user_id: raw.owner_user_id ?? null,
    owner_username: raw.owner_username ?? null,
    in_stillness: Boolean(raw.in_stillness),
    has_boundary: Boolean(raw.has_boundary),
    has_boundary_between: Boolean(raw.has_boundary_between),
    direct_interaction_available:
      raw.direct_interaction_available !== false,
    object_id: raw.object_id ?? raw.payload?.id ?? raw.id,
  };
};

const normalizeSquareResponse = (data: any): SquareResponse => {
  const results = Array.isArray(data?.results)
    ? data.results
        .map(normalizeSquareItem)
        .filter(Boolean)
    : [];

  return {
    next: data?.next ?? null,
    results: results as SquareItem[],
  };
};

export const squareApi = {
  fetch: async (opts?: {
    cursor?: string | null;
    tab?: SquareTabKey;
    mode?: "recent" | "trending" | "for_you";
  }) => {
    const cursorToken = normalizeCursor(opts?.cursor);

    const kind =
      opts?.tab === "friends"
        ? "friends"
        : "all";

    const { data } = await baseApi.get<SquareResponse>(`${ROOT}/`, {
      params: {
        kind,
        cursor: cursorToken,
        ...(opts?.mode ? { mode: opts.mode } : {}),
      },
    });

    return normalizeSquareResponse(data);
  },
};

export default squareApi;